import { useAtom, useSetAtom } from 'jotai';
import { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router';
import { BottomSheet, Button, cx, Icon, ScrollEdge, SearchField } from '../../ui';
import { ApiStatus } from '../ApiStatus';
import { useLogout, useSession } from '../auth/useSession';
import { commandMenuOpenAtom } from '../session';
import { NavIcon } from './NavIcon';
import { PendingBadge } from './PendingBadge';
import { PersonalSettingsRow, SettingsNavigation } from './SettingsNavigation';
import { moreSheetOpenAtom } from './shellState';
import { TaskBadge } from './TaskBadge';
import { ThemeToggle } from './ThemeToggle';

export type PhoneDestination = 'home' | 'data' | 'tasks' | 'more' | 'search';

/**
 * S1-7: the kit's four-item capsule and separate search circle, using our own symbols.
 *
 * THREE OF THE FOUR ARE LINKS, because three of them are stack roots (round 3, A5):
 * Home, Data and Tasks each open their own screen and a record pushes on top of it. More
 * stays a button, because More is a shelf of settings panes rather than a stack.
 */
export function PhoneTabBar({
  active,
  onMore,
  onSearch,
  preview = false,
}: {
  readonly active: PhoneDestination;
  readonly onMore: () => void;
  readonly onSearch: () => void;
  readonly preview?: boolean;
}) {
  const item = (name: PhoneDestination) => cx('shell-tab', active === name && 'shell-tab-selected');
  return (
    <>
      {/*
        THE BAR'S OWN SCROLL EDGE (SH-A11). The tab bar floats over the content and the
        content runs beneath it, which is the one case Apple says the effect is for: "Only
        use a scroll edge effect when a scroll view is behind floating interface elements"
        (hig: scroll-views). Soft, because that is the iOS default, and outside the `nav`
        so the bar's own capsule is not what carries it. It is not rendered for a gallery
        specimen, which stands in the page rather than over it.
      */}
      {!preview && (
        <div className="shell-phone-chrome-edge">
          {<ScrollEdge host="phone-tab-bar" edge="bottom" />}
        </div>
      )}
      <nav
        aria-label="Main"
        // THE SAME DECISION THE WINDOW TOOLBAR CARRIES (D4, wave 4 round 2 item 11). A
        // floating bar has content under it at rest by construction - that is the scroll
        // edge working - so the rule is told once, here, instead of by every paragraph
        // that scrolls beneath it. What is NOT a decision is the END of the scroll: the
        // content inset keeps the last line clear, and check (f) holds it.
        data-measure-ignore="covered-text:the scroll edge, and content passing UNDER this floating bar is the mechanism working; the content inset is what keeps the LAST line clear (docs/ios26-mechanisms.md)"
        className={cx('shell-tab-bar', preview && 'shell-tab-bar-preview')}
      >
        <div className="shell-tabs-capsule">
          <NavLink to="/" end className={item('home')}>
            <Icon name="home" />
            <span>Home</span>
          </NavLink>
          <NavLink to="/data" className={item('data')}>
            <Icon name="table" />
            <span>Data</span>
          </NavLink>
          <NavLink to="/tasks" className={item('tasks')}>
            {/* The badge is pinned to the SYMBOL, so the host is the symbol's own box. */}
            <span className="shell-tab-badge-host">
              <Icon name="success" />
              <TaskBadge variant="tab" />
            </span>
            <span>Tasks</span>
          </NavLink>
          <button type="button" className={item('more')} onClick={onMore}>
            <Icon name="more-horizontal" />
            <span>More</span>
          </button>
        </div>
        <button
          type="button"
          className={cx('shell-search-tab', active === 'search' && 'shell-tab-selected')}
          onClick={onSearch}
          aria-label="Search"
        >
          <Icon name="search" />
        </button>
      </nav>
    </>
  );
}

/** Data is a screen of its own (A5); the search dialog remains, and More combines the two previous settings/account sheets. */
export function MobileNav() {
  const [moreOpen, setMoreOpen] = useAtom(moreSheetOpenAtom);
  const [query, setQuery] = useState('');
  const [searchOpen] = useAtom(commandMenuOpenAtom);
  const openSearch = useSetAtom(commandMenuOpenAtom);
  const location = useLocation();
  const session = useSession();
  const logout = useLogout();
  const navigate = useNavigate();
  const active: PhoneDestination = searchOpen
    ? 'search'
    : moreOpen || location.pathname.startsWith('/settings')
      ? 'more'
      : location.pathname === '/data' ||
          location.pathname.startsWith('/fulfillment') ||
          location.pathname.startsWith('/objects') ||
          location.pathname.startsWith('/reports')
        ? 'data'
        : location.pathname === '/tasks' || location.pathname === '/pending'
          ? 'tasks'
          : 'home';
  return (
    <>
      <PhoneTabBar
        active={active}
        onMore={() => setMoreOpen(true)}
        onSearch={() => openSearch(true)}
      />
      <BottomSheet open={moreOpen} onOpenChange={setMoreOpen} title="More">
        <div className="shell-more-list">
          <SettingsNavigation query={query} onNavigate={() => setMoreOpen(false)} />
          <NavLink to="/pending" className="shell-nav-row" onClick={() => setMoreOpen(false)}>
            <NavIcon icon="pending" />
            <span className="flex-1">Pending changes</span>
            <PendingBadge />
          </NavLink>
          {/* The control names itself now (SH-A6): it is a labelled row, so the sheet's
              own "Appearance" line beside it would have said the word twice. */}
          {/* The person's own pane stands with Appearance and Sign out (round 2, F3). */}
          <PersonalSettingsRow onNavigate={() => setMoreOpen(false)} />
          <ThemeToggle />
          <Button
            variant="ghost"
            icon="log-out"
            loading={logout.isPending}
            onClick={() =>
              logout.mutate(undefined, {
                onSettled: () => {
                  setMoreOpen(false);
                  void navigate('/login', { replace: true });
                },
              })
            }
          >
            Sign out
          </Button>
          <span className="sr-only">{session.data?.name}</span>
          <ApiStatus />
          {/* THE PHONE'S SETTINGS LIST IS ITS SIDEBAR (SH-3), so its search is the kit's
              sidebar field - the same 44px capsule the desktop sidebar draws, in the
              context the primitive declares (wave 4 round 1, C2-18). */}
          <SearchField
            context="sidebar"
            className="shell-more-search"
            label="Search settings"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>
      </BottomSheet>
    </>
  );
}
